/**
 * Headless single-select controller.
 */
import { vanillaReactivity } from "@modyra/core";
import { staysOpen } from "../transitions.js";
import { filterOptionsByQuery, defaultOptionKey } from "../options-utils.js";
import { defaultWidgetIdFactory } from "../ids.js";
import { projectSelectA11y } from "./select-a11y.js";
import { variantOf } from "../structure.js";
import { selectNextActiveKey } from "./select-keyboard.js";
import { optionsWithUnrecognizedValue, sameChoice } from "../options-reconciliation.js";
export function createSelectController(options, reactivity = vanillaReactivity()) {
    const { widgetId, options: initialOptions, keyFor = (option) => defaultOptionKey(option.value), value: initialValue = null, disabled: initialDisabled = false, readonly: initialReadonly = false, invalid: initialInvalid = false, loading: initialLoading = false, onChange, } = options;
    const idFactory = defaultWidgetIdFactory;
    /** What the caller declared. */
    /**
     * What the caller declared, replaced rather than emptied and refilled.
     *
     * The painted list is a signal holding this array, and a subscriber is told a signal changed by
     * the value being a different one. Rewriting this array in place made the new list *the same
     * array* the signal already held: nothing was told, so a page kept the options it had until some
     * other change happened to redraw it.
     */
    let allOptions = [...initialOptions];
    /**
     * What is painted: the declared list, plus a held value the list does not contain.
     *
     * A signal rather than a variable: the list changes when options are replaced *and* when the
     * selection moves, and everything drawn from it — the state, the filtered list, the view's parts
     * — has to be told. It used to be told by accident, because replacing the options cleared the
     * selection; keeping the selection removed that accident.
     */
    const paintedOptions = reactivity.signal(allOptions);
    const optionByKey = new Map();
    const valueToKey = new Map();
    /**
     * The label each key was last painted with.
     *
     * A value the list stops offering is kept — that is this widget's rule — and it has to be shown as
     * something a user can read. The list held that option a moment ago, so its own label is the
     * honest name for it: a refetch that drops Ada leaves "Ada" on screen rather than a stringified
     * value. A value that was never in any list, from a draft or a patch, falls back to the shared
     * readable form.
     */
    const labelSeen = new Map();
    /**
     * The key each painted option was given, by the option itself.
     *
     * A value repeated in a list is a document defect the parser reports, and it is not a reason for
     * one of the options to disappear: the second gets a key of its own so the list can show it and a
     * reference can name it. Selection still follows the value, which is what a person chose.
     */
    const keyByOption = new Map();
    /** The key this option was given while the list was indexed. */
    const keyOf = (option) => keyByOption.get(option) ?? keyFor(option);
    function rebuildOptionIndex(selected) {
        const painted = optionsWithUnrecognizedValue(allOptions, selected, (value) => labelSeen.get(keyFor({ value, label: "" })) ?? readableFallback(value));
        paintedOptions.set(painted);
        optionByKey.clear();
        valueToKey.clear();
        keyByOption.clear();
        for (const option of painted) {
            const base = keyFor(option);
            // A key already taken by an earlier option with the same value: the second one is still an
            // option, and an element with a duplicate id is one no reference can point at.
            let key = base;
            for (let repeat = 2; optionByKey.has(key); repeat += 1)
                key = `${base}%23${repeat}`;
            keyByOption.set(option, key);
            optionByKey.set(key, option);
            // The first option carrying a value is the one a value selects: two options that say the same
            // thing are one choice, whatever the list shows.
            if (!valueToKey.has(option.value))
                valueToKey.set(option.value, key);
            labelSeen.set(key, option.label);
        }
    }
    rebuildOptionIndex(initialValue);
    /**
     * The key of the option a value is, by the contract's `sameChoice`.
     *
     * Identity first, because it is the common case and a Map answers it at once. A value that comes
     * back from a draft, a refetch or an import is not the option object the list was built from — an
     * object returns as a copy, a number read from JSON as text — and it is still the same choice, so
     * the painted options are asked the contract's question before the value is called unknown.
     */
    const keyForValue = (value) => {
        if (value === null)
            return null;
        const direct = valueToKey.get(value);
        if (direct !== undefined)
            return direct;
        const match = paintedOptions().find((option) => sameChoice(value, option.value));
        return match === undefined ? null : keyOf(match);
    };
    const valueForKey = (key) => key === null ? null : optionByKey.get(key)?.value ?? null;
    const filteredOptions = (query) => filterOptionsByQuery(paintedOptions(), query);
    const visibleKeys = (query) => filteredOptions(query).map(keyOf);
    const navigableKeys = (query) => filteredOptions(query)
        .filter((o) => !o.disabled)
        .map(keyOf);
    const open = reactivity.signal(false);
    const query = reactivity.signal("");
    const activeKey = reactivity.signal(null);
    const selectedKey = reactivity.signal(keyForValue(initialValue));
    const disabled = reactivity.signal(initialDisabled);
    const readonly = reactivity.signal(initialReadonly);
    const invalid = reactivity.signal(initialInvalid);
    const required = reactivity.signal(options.required ?? false);
    const touched = reactivity.signal(false);
    const dirty = reactivity.signal(false);
    const loading = reactivity.signal(initialLoading);
    // What the trigger may describe itself by. The renderer owns the answer because it owns whether
    // the elements are on screen; the default is the resting field — a description and no errors.
    const errorsVisible = reactivity.signal(false);
    const supportingText = reactivity.signal(options.supportingText ?? null);
    // Eager by default: the popup is in the document whether or not it is open, which is what every
    // renderer did before a lazily-mounted one existed.
    const popupRendered = reactivity.signal(true);
    const state = reactivity.computed(() => ({
        options: paintedOptions(),
        optionKeys: paintedOptions().map(keyOf),
        open: staysOpen(open(), disabled()),
        query: query(),
        activeKey: activeKey(),
        selectedValue: valueForKey(selectedKey()),
        selectedKey: selectedKey(),
        disabled: disabled(),
        readonly: readonly(),
        invalid: invalid(),
        touched: touched(),
        dirty: dirty(),
        loading: loading(),
    }));
    const view = reactivity.computed(() => {
        const q = query();
        const currentActiveKey = activeKey();
        const currentSelectedKey = selectedKey();
        const currentOpen = open();
        const a11y = projectSelectA11y({
            widgetId,
            open: currentOpen,
            activeKey: currentActiveKey,
            selectedKey: currentSelectedKey,
            disabled: disabled(),
            readonly: readonly(),
            invalid: invalid(),
            required: required(),
            loading: loading(),
            idFactory,
            visibleKeys: visibleKeys(q),
            errorsVisible: errorsVisible(),
            supportingText: supportingText(),
            popupRendered: popupRendered(),
            // Only where the caller said. Read from a `searchable` nobody set, `variantOf` answers
            // "native" — which is the contract's answer for a select that does not filter, and the wrong
            // answer for a renderer that has not been told to draw one yet: it would take the combobox
            // relation off a combobox still on the page.
            ...(options.searchable === undefined
                ? {}
                : { variant: variantOf("select", { searchable: options.searchable }) }),
        });
        // A null prototype, because these keys are data: an option valued `__proto__` assigned into a
        // plain object sets that object's prototype instead of adding a member, so the part vanished and
        // the renderer was handed `undefined` — the control disappeared from the page mid-draw.
        const parts = Object.create(null);
        // What the state paints, not what the caller declared. `MdySelectState.options` says a renderer
        // paints *this* rather than the list it was handed — so building parts from the declared list
        // left the survivor with no part: an element inside a listbox with no id, no `role="option"`
        // and no `aria-selected`, and the one entry the user needs in order to replace their value.
        for (const option of paintedOptions()) {
            const key = keyOf(option);
            parts[key] = a11y.option(key, option.disabled === true);
        }
        return {
            root: a11y.trigger,
            parts: {
                trigger: a11y.trigger,
                search: a11y.search,
                options: a11y.options,
                ...parts,
            },
        };
    });
    function setActive(key, commands) {
        activeKey.set(key);
        if (key) {
            commands.push({
                type: "scroll-into-view",
                target: { part: "option", key },
            });
        }
    }
    function selectKey(key, commands) {
        if (key !== null && optionByKey.get(key)?.disabled)
            return;
        selectedKey.set(key);
        // One act, two flags: the value is no longer what it arrived as, and the field has had an
        // answer — which is what decides whether a refusal is shown to anybody yet. ADR 0167.
        dirty.set(true);
        touched.set(true);
        commands.push({ type: "emit-change" }, { type: "mark-touched" });
        onChange?.(valueForKey(key));
    }
    function close(restoreFocus, commands) {
        if (!open())
            return;
        open.set(false);
        activeKey.set(null);
        query.set("");
        // Opening the list and closing it without choosing is an act on the value — the panel's version
        // of typing and deleting. The person saw what was on offer and took none of it, which is an
        // answer, where merely passing through the field is not. Touched and not dirty: nothing about
        // the value changed. ADR 0167.
        touched.set(true);
        commands.push({ type: "close-overlay" }, { type: "mark-touched" });
        if (restoreFocus) {
            commands.push({
                type: "restore-focus",
                target: { part: "trigger" },
            });
        }
    }
    function dispatch(intent) {
        const commands = [];
        if (disabled() || readonly())
            return commands;
        switch (intent.type) {
            case "open": {
                if (!open()) {
                    open.set(true);
                    const keys = navigableKeys(query());
                    const next = selectedKey() && keys.includes(selectedKey())
                        ? selectedKey()
                        : keys[0] ?? null;
                    setActive(next, commands);
                    commands.push({
                        type: "open-overlay",
                        anchor: { part: "trigger" },
                    });
                }
                break;
            }
            case "close": {
                close(intent.restoreFocus, commands);
                break;
            }
            case "move": {
                // A closed list has nothing to move through, and opening here would be a second way to open
                // beside the keyboard policy — which already answers `ArrowDown` on a collapsed combobox
                // with `open`. Two paths to one behaviour means neither can be changed on its own — remove
                // the policy's rule and this one still opens, leaving the suite green on a contract that no
                // longer says so.
                if (!open())
                    break;
                const next = selectNextActiveKey(intent.target, activeKey(), navigableKeys(query()));
                if (next !== null) {
                    setActive(next, commands);
                }
                break;
            }
            case "activate": {
                // Only over what is navigable: activating a filtered-out option would point
                // `aria-activedescendant` at an element that is not in the list.
                if (!open())
                    break;
                if (navigableKeys(query()).includes(intent.optionKey)) {
                    setActive(intent.optionKey, commands);
                }
                break;
            }
            case "select": {
                selectKey(intent.optionKey, commands);
                close(true, commands);
                break;
            }
            case "search": {
                query.set(intent.query);
                const keys = navigableKeys(intent.query);
                const next = keys[0] ?? null;
                if (!open()) {
                    open.set(true);
                    commands.push({
                        type: "open-overlay",
                        anchor: { part: "trigger" },
                    });
                }
                setActive(next, commands);
                break;
            }
            case "blur": {
                // A leaving is not an answer: focus arriving and going is an act on attention, and a form
                // that treats a traversal as a decision announces "invalid" to somebody who is reading it.
                // What makes this field answerable is a change to its value. ADR 0167.
                //
                // Closes without restoring focus. Focus has already gone where the user sent it — a Tab, a
                // click on another control — and pulling it back to the trigger takes it off whatever they
                // just reached for. Escape is the opposite case and restores deliberately: there the user
                // is still in the widget and has nowhere else to be.
                close(false, commands);
                break;
            }
            case "focus": {
                // No state change required; adapter may track focus visually.
                break;
            }
        }
        return commands;
    }
    function setValue(value) {
        // The index is rebuilt around the value, so a value the declared options do not contain gets an
        // option of its own — and therefore a key, a chip and a way for the user to replace it.
        rebuildOptionIndex(value);
        selectedKey.set(keyForValue(value));
    }
    function setOptions(nextOptions) {
        const selected = valueForKey(selectedKey());
        allOptions = [...nextOptions];
        // The selection survives the list changing: options arriving, being filtered or being replaced
        // are not a user edit, and a value the new list does not name keeps an option of its own rather
        // than being dropped. What refuses such a value is a rule — `oneOf` — not the widget.
        rebuildOptionIndex(selected);
        selectedKey.set(keyForValue(selected));
        // What the keyboard was pointing at is a different question from what is selected, and the
        // pointer has no option of its own to keep: `aria-activedescendant` names an element, so a key
        // the new list does not have names nothing on the page.
        const pointing = activeKey();
        if (pointing !== null && !optionByKey.has(pointing))
            activeKey.set(null);
    }
    function setOpen(nextOpen) {
        open.set(nextOpen);
        if (!nextOpen) {
            activeKey.set(null);
            query.set("");
        }
    }
    function setDisabled(nextDisabled) {
        disabled.set(nextDisabled);
    }
    function setReadonly(nextReadonly) {
        readonly.set(nextReadonly);
    }
    function setRequired(next) {
        required.set(next);
    }
    function setInvalid(nextInvalid) {
        invalid.set(nextInvalid);
    }
    function setDescribedBy(next) {
        if (next.errorsVisible !== undefined)
            errorsVisible.set(next.errorsVisible);
        if (next.supportingText !== undefined)
            supportingText.set(next.supportingText);
    }
    function setPopupRendered(rendered) {
        popupRendered.set(rendered);
    }
    function setLoading(nextLoading) {
        loading.set(nextLoading);
    }
    function destroy() {
        // Signals are released by dropping references; no effects to clean here.
    }
    return {
        state,
        view,
        dispatch,
        setValue,
        setOptions,
        setOpen,
        setDisabled,
        setReadonly,
        setInvalid,
        setRequired,
        setLoading,
        setDescribedBy,
        setPopupRendered,
        destroy,
    };
}
/** A value with no option to name it: readable, and never `[object Object]`. */
function readableFallback(value) {
    return typeof value === "object" && value !== null ? defaultOptionKey(value) : String(value);
}
